import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import CartSidebar from './cart/CartSidebar';
import RamiCareLogo from './brand/RamiCareLogo';

const B2BNavbar = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { toggleCart, getItemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isAgent = user.role === 'field_agent';
  const isAdmin = user.role === 'admin';

  return (
    <>
    <nav className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo & Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <RamiCareLogo size="md" showText={false} />
              <span className="mr-3 text-white text-xl font-bold">Rami-Care</span>
              <span className="mr-2 text-emerald-100 text-xs font-medium hidden sm:block">
                | פורטל עסקי
              </span>
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-reverse space-x-4">
            <Link
              to="/"
              className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
            >
              <HomeIcon className="h-5 w-5 ml-2" />
              דף הבית
            </Link>

            {isAgent && (
              <>
                <Link
                  to="/agent"
                  className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  פורטל סוכן
                </Link>
                <Link
                  to="/agent/companies"
                  className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <DocumentTextIcon className="h-5 w-5 ml-2" />
                  חברות
                </Link>
              </>
            )}

            {!isAgent && (
              <>
                <Link
                  to="/trade"
                  className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  מסחר
                </Link>
                <button
                  onClick={toggleCart}
                  className="relative text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                  aria-label="עגלת קניות"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  עגלה
                  {getItemCount() > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold text-white">
                      {getItemCount()}
                    </span>
                  )}
                </button>
                <Link
                  to="/orders"
                  className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <DocumentTextIcon className="h-5 w-5 ml-2" />
                  הזמנות
                </Link>
                <Link
                  to="/payments"
                  className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <BanknotesIcon className="h-5 w-5 ml-2" />
                  תשלומים
                </Link>
              </>
            )}

            {isAdmin && (
              <Link
                to="/admin"
                className="text-white hover:bg-emerald-700 px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <Cog6ToothIcon className="h-5 w-5 ml-2" />
                אדמין
              </Link>
            )}

            {/* User Menu */}
            <div className="flex items-center border-r border-emerald-400 pr-4 mr-4">
              <span className="text-white text-sm ml-3">
                {user.name || user.email}
              </span>
              <button
                onClick={handleLogout}
                className="text-white hover:bg-emerald-700 p-2 rounded-md flex items-center"
                title="התנתק"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-emerald-700 p-2 rounded-md"
              aria-label="תפריט"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile Menu Overlay - with scroll */}
    {mobileMenuOpen && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        onClick={() => setMobileMenuOpen(false)}
      >
        <div 
          className="fixed right-0 top-0 h-full w-64 bg-white shadow-xl overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          dir="rtl"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 flex justify-between items-center">
            <div>
              <p className="text-white font-bold text-sm">
                {user.name || user.email}
              </p>
              <p className="text-emerald-100 text-xs">
                {isAgent ? 'סוכן שטח' : isAdmin ? 'מנהל' : 'לקוח עסקי'}
              </p>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-white hover:bg-emerald-700 p-2 rounded-md"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Menu Items - Extra padding for bottom nav */}
          <div className="py-4 pb-40">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
            >
              <HomeIcon className="h-5 w-5 ml-2" />
              דף הבית
            </Link>

            {isAgent && (
              <>
                <Link
                  to="/agent"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  פורטל סוכן
                </Link>
                <Link
                  to="/agent/companies"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <DocumentTextIcon className="h-5 w-5 ml-2" />
                  חברות
                </Link>
              </>
            )}

            {!isAgent && (
              <>
                <Link
                  to="/trade"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  מסחר
                </Link>
                <button
                  onClick={() => {
                    toggleCart();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 text-right"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  עגלה
                  {getItemCount() > 0 && (
                    <span className="mr-2 px-2 py-0.5 rounded-full bg-red-500 text-xs font-bold text-white">
                      {getItemCount()}
                    </span>
                  )}
                </button>
                <Link
                  to="/orders"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <DocumentTextIcon className="h-5 w-5 ml-2" />
                  הזמנות
                </Link>
                <Link
                  to="/payments"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <BanknotesIcon className="h-5 w-5 ml-2" />
                  תשלומים
                </Link>
              </>
            )}

            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
              >
                <Cog6ToothIcon className="h-5 w-5 ml-2" />
                אדמין
              </Link>
            )}

            {/* Logout Button - Always at bottom with extra spacing for bottom nav */}
            <div className="border-t mt-4 pt-4 mb-20">
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center px-4 py-4 text-red-600 hover:bg-red-50 font-bold rounded-lg mx-2 border-2 border-red-200 shadow-sm"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6 ml-2" />
                <span className="text-lg">התנתק מהמערכת</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
      
      {/* Cart Sidebar */}
      <CartSidebar />
    </>
  );
};

export default B2BNavbar;

